import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Card, CardBody, CardHeader, CardTitle, Spinner, Table } from "reactstrap";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";

import { apiFetch, API } from "../../services/api";
import BlockIpModal from "../../components/security/BlockIpModal";
import { alertTypeLabel, alertTypeColor, formatDateTime, apiErrorMessage } from "../../helpers/security";

const ALERTS_LIMIT = 200;

// Identité d'une alerte : la table est à clé composée (alertKey + sentAt), une
// même clé peut revenir après le cooldown.
const alertID = (alert) => String(alert.alertKey || "") + "|" + String(alert.sentAt || "");

// Journal de sécurité : toutes les alertes émises par le robot (Google Chat et
// pikachu lisent la même table `security_alerts`), plus les blocages d'IP en
// cours. « Marquer comme traitée » est la mémoire du traitement : ce qui est
// traité sort de la vue par défaut et ne pollue plus les constats ouverts de la
// vue d'ensemble.
export function SecurityJournal() {
    const [alerts, setAlerts] = useState(null);
    const [alertsError, setAlertsError] = useState(null);
    const [openOnly, setOpenOnly] = useState(true);
    const [expanded, setExpanded] = useState({});
    const [busyID, setBusyID] = useState(null);

    const [blocks, setBlocks] = useState(null);
    const [blocksError, setBlocksError] = useState(null);
    const [busyIp, setBusyIp] = useState(null);
    const [adding, setAdding] = useState(false);

    const loadAlerts = useCallback(() => {
        apiFetch(API.back + "/security/alerts?limit=" + ALERTS_LIMIT, { method: "GET", market: false })
            .then((res) => {
                if (!res.ok) throw new Error("HTTP " + res.status);
                return res.json();
            })
            .then((data) => {
                setAlerts(Array.isArray(data) ? data : []);
                setAlertsError(null);
            })
            .catch((err) => {
                console.log(err);
                setAlertsError("Journal indisponible (" + err.message + ").");
                setAlerts([]);
            });
    }, []);

    const loadBlocks = useCallback(() => {
        apiFetch(API.back + "/security/blocks", { method: "GET", market: false })
            .then((res) => {
                if (!res.ok) throw new Error("HTTP " + res.status);
                return res.json();
            })
            .then((data) => {
                setBlocks(Array.isArray(data) ? data : []);
                setBlocksError(null);
            })
            .catch((err) => {
                console.log(err);
                setBlocksError("Blocages indisponibles (" + err.message + ").");
                setBlocks([]);
            });
    }, []);

    useEffect(() => {
        loadAlerts();
        loadBlocks();
    }, [loadAlerts, loadBlocks]);

    const ack = (row) => {
        const id = alertID(row);
        setBusyID(id);
        apiFetch(API.back + "/security/alerts/ack", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ alertKey: row.alertKey, sentAt: row.sentAt }),
        })
            .then((res) => {
                if (!res.ok) {
                    return res.text().then((body) => Promise.reject(new Error(apiErrorMessage(res.status, body))));
                }
                setBusyID(null);
                loadAlerts();
            })
            .catch((err) => {
                console.log(err);
                setBusyID(null);
                window.alert("Impossible de marquer l'alerte comme traitée : " + err.message);
            });
    };

    const unblock = (ip) => {
        setBusyIp(ip);
        apiFetch(API.back + "/security/blocks?ip=" + encodeURIComponent(ip), { method: "DELETE" })
            .then((res) => {
                if (!res.ok) {
                    return res.text().then((body) => Promise.reject(new Error(apiErrorMessage(res.status, body))));
                }
                setBusyIp(null);
                loadBlocks();
            })
            .catch((err) => {
                console.log(err);
                setBusyIp(null);
                window.alert("Déblocage impossible : " + err.message);
            });
    };

    const rows = (alerts || []).filter((alert) => (openOnly ? alert.ack !== true : true));
    const openCount = (alerts || []).filter((alert) => alert.ack !== true).length;

    return (
        <div>
            <Card className="flex-fill w-100">
                <CardHeader>
                    <div className="d-flex justify-content-between align-items-start">
                        <div>
                            <CardTitle tag="h5" className="mb-1">
                                Sécurité — journal
                            </CardTitle>
                            <div className="text-muted small">
                                Alertes émises par la surveillance (WAF, APIs, connexions, GuardDuty, CloudTrail).
                                {alerts ? " " + openCount + " non traitée(s)." : ""}
                            </div>
                        </div>
                        <div className="d-flex align-items-center">
                            <Form.Check
                                type="checkbox"
                                id="security-open-only"
                                className="mr-3"
                                label="Non traitées seulement"
                                checked={openOnly}
                                onChange={(e) => setOpenOnly(e.target.checked)}
                            />
                            <Link to="/security/overview" className="btn btn-outline-secondary btn-sm">
                                Vue d'ensemble
                            </Link>
                        </div>
                    </div>
                </CardHeader>
                <CardBody>
                    {alertsError ? <div className="alert alert-danger">{alertsError}</div> : null}
                    {!alerts && !alertsError ? (
                        <div className="d-flex align-items-center">
                            <Spinner color="primary" size="sm" />
                            <span className="text-muted ml-2">Chargement…</span>
                        </div>
                    ) : null}
                    {alerts && rows.length === 0 && !alertsError ? (
                        <p className="text-muted mb-0">
                            {openOnly ? "Aucune alerte non traitée. " : "Aucune alerte. "}
                            {openOnly && (alerts || []).length > 0
                                ? "Décocher le filtre pour relire les alertes traitées."
                                : ""}
                        </p>
                    ) : null}
                    {rows.length > 0 ? (
                        <div style={{ overflowX: "auto" }}>
                            <Table responsive size="sm" className="mb-0">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Type</th>
                                        <th>Titre</th>
                                        <th>Statut</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((alert) => {
                                        const id = alertID(alert);
                                        const isOpen = expanded[id] === true;
                                        const body = String(alert.body || "").trim();
                                        return (
                                            <React.Fragment key={id}>
                                                <tr>
                                                    <td className="text-muted small">{formatDateTime(alert.sentAt)}</td>
                                                    <td>
                                                        <Badge color={alertTypeColor(alert.type)}>
                                                            {alertTypeLabel(alert.type)}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <div>{alert.title || "(sans titre)"}</div>
                                                        {body !== "" ? (
                                                            <Button
                                                                variant="link"
                                                                size="sm"
                                                                className="p-0"
                                                                onClick={() =>
                                                                    setExpanded((current) => ({
                                                                        ...current,
                                                                        [id]: !isOpen,
                                                                    }))
                                                                }
                                                            >
                                                                {isOpen ? "Masquer le détail" : "Voir le détail"}
                                                            </Button>
                                                        ) : null}
                                                    </td>
                                                    <td>
                                                        {alert.ack === true ? (
                                                            <span>
                                                                <Badge color="success">Traitée</Badge>
                                                                {alert.ackBy ? (
                                                                    <div className="text-muted small">
                                                                        {alert.ackBy}
                                                                        {alert.ackAt ? " · " + formatDateTime(alert.ackAt) : ""}
                                                                    </div>
                                                                ) : null}
                                                            </span>
                                                        ) : (
                                                            <Badge color="secondary">Non traitée</Badge>
                                                        )}
                                                    </td>
                                                    <td className="table-action">
                                                        {alert.ack === true ? null : (
                                                            <Button
                                                                size="sm"
                                                                variant="outline-primary"
                                                                disabled={busyID === id}
                                                                onClick={() => ack(alert)}
                                                            >
                                                                {busyID === id ? "En cours…" : "Marquer comme traitée"}
                                                            </Button>
                                                        )}
                                                    </td>
                                                </tr>
                                                {isOpen && body !== "" ? (
                                                    <tr>
                                                        <td colSpan={5} className="bg-light">
                                                            <pre
                                                                className="mb-0 small"
                                                                style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                                                            >
                                                                {body}
                                                            </pre>
                                                        </td>
                                                    </tr>
                                                ) : null}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    ) : null}
                </CardBody>
            </Card>

            <Card className="flex-fill w-100">
                <CardHeader>
                    <div className="d-flex justify-content-between align-items-start">
                        <div>
                            <CardTitle tag="h5" className="mb-1">
                                Blocages d'IP
                            </CardTitle>
                            <div className="text-muted small">
                                Adresses refusées par le WAF. Les blocages temporaires sont retirés automatiquement à
                                l'échéance.
                            </div>
                        </div>
                        <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
                            Ajouter une IP
                        </Button>
                    </div>
                </CardHeader>
                <CardBody>
                    {blocksError ? <div className="alert alert-danger">{blocksError}</div> : null}
                    {!blocks && !blocksError ? (
                        <div className="d-flex align-items-center">
                            <Spinner color="primary" size="sm" />
                            <span className="text-muted ml-2">Chargement…</span>
                        </div>
                    ) : null}
                    {blocks && blocks.length === 0 && !blocksError ? (
                        <p className="text-muted mb-0">Aucune IP bloquée pour le moment.</p>
                    ) : null}
                    {blocks && blocks.length > 0 ? (
                        <div style={{ overflowX: "auto" }}>
                            <Table responsive size="sm" className="mb-0">
                                <thead>
                                    <tr>
                                        <th>IP</th>
                                        <th>Expiration</th>
                                        <th>Auteur</th>
                                        <th>Motif</th>
                                        <th>Créé le</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {blocks.map((block) => (
                                        <tr key={block.ip}>
                                            <td>{block.ip}</td>
                                            <td>
                                                {String(block.expiresAt || "").trim() === "" ? (
                                                    <Badge color="danger">Définitif</Badge>
                                                ) : (
                                                    formatDateTime(block.expiresAt)
                                                )}
                                            </td>
                                            <td className="text-muted small">{block.author || "—"}</td>
                                            <td className="text-muted small">{block.reason || "—"}</td>
                                            <td className="text-muted small">{formatDateTime(block.createdAt)}</td>
                                            <td className="table-action">
                                                <Button
                                                    size="sm"
                                                    variant="outline-secondary"
                                                    disabled={busyIp === block.ip}
                                                    onClick={() => unblock(block.ip)}
                                                >
                                                    {busyIp === block.ip ? "En cours…" : "Débloquer"}
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : null}
                </CardBody>
            </Card>

            {adding ? (
                <BlockIpModal
                    isOpen={true}
                    ip=""
                    closeModal={() => setAdding(false)}
                    onBlocked={() => {
                        setAdding(false);
                        loadBlocks();
                    }}
                />
            ) : null}
        </div>
    );
}

export default SecurityJournal;
